/**
 * ZIZOV Jewellery Finder  v1.6
 * ──────────────────────────────────────────────────────────────
 * Universal jewellery advisor: Rings · Pendants · Earrings · Bracelets
 * 7-step adaptive quiz (shape step skipped for earrings/bracelets)
 * Step flow: Jewellery Type → Diamond → Occasion → Style → Metal → Shape* → Budget
 *
 * v1.2 deep-audit against 721-product export (2026-03-08) — all known bugs fixed:
 *
 *  CRITICAL FIXES:
 *  ✓ Dead code block removed — old CFG block (292 lines) was floating as expression
 *    statements after the new CFG closed; now cleanly removed
 *  ✓ COLLECTION_META_MAP added — stamps every product with _jfType/_jfDiamond at
 *    fetch time using authoritative collection membership (not tags/product_type)
 *  ✓ startFetch() stamps _jfType/_jfDiamond onto products from COLLECTION_META_MAP
 *  ✓ scoreJewelleryType() rewritten:
 *      — collection match → +4.5, confirmed mismatch → -4.0 (beats full budget score)
 *      — scoreProduct() short-circuits at -99 on confirmed mismatch
 *      — eliminates earring/ring confusion (44/45 earrings incorrectly had "Rings" tag)
 *  ✓ scoreDiamond() added — symmetrical: natural scores +1.5 by absence of lab signals;
 *    collection membership gives ±3.0; keyword fallback ±2.5
 *  ✓ scoreMetal() rewritten — uses fields.metals Set (exact option2 values from variants);
 *    eliminates old yellowGold/variants field collision bug
 *  ✓ FIELD_WEIGHTS reduced to { title: 3, handle: 2 } — tags removed because Zizov
 *    tags contain ONLY delivery times + category names (zero scoring value)
 *  ✓ buildFields() cleaned — removes product_type (Dutch, unreliable), tags (useless),
 *    variants text; adds metals Set, jfType, jfDiamond
 *  ✓ product_type NOT requested in API fields (bandwidth saving)
 *  ✓ rankProducts() shows top-24 fallback when all questions skipped (was empty)
 *  ✓ compare_at_price uses the metal-matched variant, not always variants[0]
 *  ✓ variantId declared before use in renderResults (was a TDZ bug)
 *
 *  KEYWORD / CONFIG FIXES:
 *  ✓ Removed 'invisible' from modern style (93/721 products → massive false positives)
 *  ✓ Removed 'horizontal' from bar style (too generic — 'horizontal oval' = shape)
 *  ✓ Removed bare 'drop' from drop style ('teardrop' = pear shape; 'WaterDrop' mismatch)
 *  ✓ Removed 'eternity' from anniversary (ambiguous with wedding)
 *  ✓ Removed 'classic' from everyday occasions (too broad — matches 300+ products)
 *  ✓ Added ' (lg)' keyword for lab-grown (271 products have it — most reliable signal)
 *  ✓ Added 'pearshape' (no dash) — appears in actual titles
 *  ✓ Dutch keywords removed from shape scoring (don't appear in scored title/handle fields)
 *  ✓ gemstone-rings excluded from COLLECTION_META_MAP (contains 2 earrings → cross-type)
 *  ✓ personalised excluded (16 pendants + 4 rings → cross-type collection)
 */

(function () {
  'use strict';

  /* ============================================================
     LABELS — single source of truth for all display strings
     ============================================================ */
  const LABELS = {
    jewelleryType: {
      ring:     'Ring',
      pendant:  'Pendant',
      earrings: 'Earrings',
      bracelet: 'Bracelet'
    },
    diamond: {
      natural:  'Natural Diamond',
      labGrown: 'Lab Grown'
    },
    occasion: {
      engagement:  'Engagement',
      wedding:     'Wedding / Bridal',
      anniversary: 'Anniversary',
      birthday:    'Birthday / Gift',
      everyday:    'Everyday'
    },
    style: {
      // Ring styles
      solitaire:  'Solitaire',
      halo:       'Halo',
      vintage:    'Vintage',
      modern:     'Modern',
      threeStone: 'Three Stone',
      sidestone:  'Side Stone',
      // Pendant / earring styles
      drop:       'Drop',
      bar:        'Bar',
      cluster:    'Cluster',
      stud:       'Stud',
      hoop:       'Hoop',
      huggie:     'Huggie',
      // Bracelet styles
      tennis:     'Tennis',
      bangle:     'Bangle',
      chain:      'Chain',
      cuff:       'Cuff',
      charm:      'Charm'
    },
    metal: {
      yellowGold: 'Yellow Gold',
      whiteGold:  'White Gold',
      roseGold:   'Rose Gold',   // no accent — matches UI label & Shopify storefront display
      // platinum: not stocked as standard — custom order only
    },
    shape: {
      round:    'Round',
      oval:     'Oval',
      emerald:  'Emerald',
      cushion:  'Cushion',
      princess: 'Princess',
      pear:     'Pear',
      marquise: 'Marquise',
      radiant:  'Square Radiant',
      heart:    'Heart'
    }
  };

  /* All valid values per criterion — used for URL param validation */
  const VALID_VALUES = {
    jewelleryType: new Set([...Object.keys(LABELS.jewelleryType), '__skip']),
    diamond:  new Set([...Object.keys(LABELS.diamond),  '__skip']),
    occasion: new Set([...Object.keys(LABELS.occasion), '__skip']),
    style:    new Set([...Object.keys(LABELS.style),    '__skip']),
    metal:    new Set([...Object.keys(LABELS.metal),    '__skip']),
    shape:    new Set([...Object.keys(LABELS.shape),    '__skip'])
  };


  /* ============================================================
     CONFIGURATION — deep-audited against 721-product export (2026-03-08)
     521 Rings · 101 Pendants · 45 Earrings · 43 Bracelets
     Metals: "Gold" (yellow) · "Rosé Gold" · "White Gold" (exact Shopify option2 values)
     ============================================================ */
  const CFG = {
    /**
     * COLLECTIONS — fetched for product discovery + shape/style stamping.
     *   Type-exclusive collections listed first (ring → pendant → earring → bracelet).
     *   Style/shape-only collections appended at the end — these may contain products
     *   already seen; the two-pass fetch logic stamps shape/style onto them without
     *   re-adding duplicates to the product array.
     */
    collections: [
      /* ── Rings · Natural ─────────────────────────────────── */
      'natural-diamond-rings',
      'natural-engagement-rings',
      'natural-diamond-solitaire-rings',
      'natural-diamond-halo-rings',
      'natural-diamond-under-halo-rings',
      'natural-diamond-side-stone-rings',
      'natural-diamond-trilogy-rings',
      'natural-diamond-toi-et-moi-rings',
      'natural-diamond-eternity-rings',
      'natural-round-brilliant-cut-diamond-collection',
      'natural-oval-cut-diamond-collection',
      'natural-emerald-cut-diamond-collection',
      'natural-cushion-cut-diamond-collection',
      'natural-pear-cut-diamond-collection',
      'natural-marquise-cut-diamond-collection',
      'natural-heart-cut-diamond-collection',
      'natural-princess-cut-diamond-collection',
      'natural-radiant-cut-diamond-collection',
      /* ── Rings · Lab-grown ───────────────────────────────── */
      'lab-grown-diamond-rings',
      'lab-grown-diamond-solitaire-rings',
      'lab-grown-diamond-halo-rings',
      'lab-grown-diamond-under-halo-rings',
      'lab-grown-diamond-side-stone-rings',
      'lab-grown-diamond-trilogy-rings',
      'lab-grown-diamond-toi-et-moi-rings',
      'lab-grown-diamond-eternity-rings',
      'exclusive-rings-lg',
      'lab-grown-round-brilliant-cut-diamond-collection',
      'lab-grown-oval-cut-diamond-collection',
      'lab-grown-emerald-cut-diamond-collection',
      'labgrown-cushion-cut-diamond-collection',
      'labgrown-pear-cut-diamond-collection',
      'lab-grown-marquise-cut-diamond-collection',
      'labgrown-princess-cut-diamond-collection',
      'lab-grown-radiant-cut-diamond-collection',
      'fancy-colour-lg',
      /* ── Rings · Style / Gemstone ────────────────────────── */
      'cocktail-rings',
      'design-rings',
      'invisible-rings',
      'initial-rings',
      'marry-by-shape',
      'pink-diamond',
      'yellow-diamond',
      'tanzanite',
      'tourmaline',
      'garnet-peridot',
      'semi-precious-gemstone-collection',
      /* ── Pendants · Natural ──────────────────────────────── */
      'natural-diamond-pendants',
      /* ── Pendants · Lab-grown ────────────────────────────── */
      'lab-grown-diamond-pendant-collection',
      /* ── Pendants · Style ────────────────────────────────── */
      'solitaire-pendants',
      'halo-pendants-lg',
      'classic-pendants-lg',
      'design-pendants',
      'special-pendants-lg',
      'daily-diamond',
      'invisible-pendant',
      'initial-pendant',
      /* ── Earrings · Natural ──────────────────────────────── */
      'natural-diamond-earrings',
      /* ── Earrings · Lab-grown ────────────────────────────── */
      'lab-grown-diamond-earrings',
      /* ── Earrings · Style ────────────────────────────────── */
      'studs',
      'studs-lg',
      'hoops',
      'hoops-lg',
      'dangle',
      'dangling-lg',
      'invisible-studs',
      'invisible-earring',
      'statements',
      /* ── Bracelets · Natural ─────────────────────────────── */
      'natural-diamond-bracelets',
      /* ── Bracelets · Lab-grown ───────────────────────────── */
      'lab-grown-diamond-bracelets',
      /* ── Bracelets · Style ───────────────────────────────── */
      'tennis-bracelet',
      'tennis-bracelets-lg',
      'bangle',
      'bangles-lg',
      'invisible-bracelet',
      'champagne-diamond',
      /* ── Style-only collections (shape/style stamps, no new product types) ── */
      /* These are fetched solely to stamp shape/style onto products already      */
      /* in the array. Duplicate products are ignored by the two-pass logic.      */
      'classic-solitaire',      // style: solitaire (rings)
      'halo-solitaire',         // style: halo (rings, natural)
      'halo-solitaire-lg',      // style: halo (rings, lab-grown)
      /* ── Gemstone & fancy colour — type-specific where safe ─────────────── */
      /* Ordered so type-exclusive collections above have already stamped the    */
      /* correct type on shared products before these are reached.               */
      'gemstone-rings',               // 69 rings + 2 earrings (earrings already stamped above)
      'precious-gemstone-collection', // 53R+8P+4E — mixed, no type stamp
      'fancy-diamond-collection',     // 21 rings + 2 bracelets + 1 pendant (others already stamped)
      'green-emerald',                // 24R+7P+1E — mixed, no type stamp
      'blue-sapphire',                // 13 rings + 1 earring (already stamped)
      'red-ruby',                     // 12R+2E+1P — mixed, no type stamp
      /* ── Cross-type · MUST be LAST so type stamps from above are never overwritten ── */
      'invisible-diamond-collection', // 60R+17P+15E+2B — no type stamp, complements invisible-* colls
    ],

    perPage:    250,
    maxResults: 24,
    /**
     * product_type is NOT requested — it's unreliable (Dutch values not consistently
     * assigned). Type detection is done via collection membership (_jfType stamp).
     */
    fields: 'id,title,handle,variants,images',

    appointmentUrl: 'https://app.acuityscheduling.com/schedule.php?owner=16861728',
    allJewelleryUrl: '/collections/all',

    STEP_KEYS: {
      1: 'jewelleryType',
      2: 'diamond',
      3: 'occasion',
      4: 'style',
      5: 'metal',
      6: 'shape',
      7: null
    },

    JEWELLERY_TYPE_KEYS: ['ring', 'pendant', 'earrings', 'bracelet'],
    METAL_KEYS: ['yellowGold', 'whiteGold', 'roseGold'],
    BESPOKE_INDEX: 5,

    /**
     * FIELD_WEIGHTS — used by scoreCriterion for occasion / style / shape.
     * product_type removed (unreliable Dutch values, not used).
     * variants removed (only Metal uses them via dedicated fields.metals Set).
     * tags removed (Zizov tags contain ONLY delivery times + category names
     *   e.g. "Rings", "1 week" — zero scoring value for style/occasion/shape).
     */
    FIELD_WEIGHTS: { title: 3, handle: 2 },

    keywords: {
      /**
       * jewelleryType — used ONLY as keyword fallback when _jfType is not set
       * (rare: products in no type-exclusive collection).
       * Uses ' ring' (space-prefixed) to avoid 'ring' matching inside 'earrings'.
       * Handles use '-ring-' which is NOT present in '-earring-'.
       */
      jewelleryType: {
        ring:     [' ring', '-ring-', 'ring-zizov'],
        pendant:  [' pendant', ' necklace', '-pendant'],
        earrings: [' earring', ' studs', ' hoops', '-earring', '-studs-', '-hoops-'],
        bracelet: [' bracelet', ' bangle', '-bracelet', '-bangle']
      },

      /**
       * diamond — used by scoreDiamond() for fallback when _jfDiamond is not set.
       * Lab-grown signals: ' (lg)' suffix in title (271 products), '-lg-' in handle.
       * Natural: absence of lab signals → assume natural (no explicit "natural" in titles).
       */
      diamond: {
        natural:  [],
        labGrown: [' (lg)', '-lg-', 'lab grown', 'lab-grown', 'labgrown', 'cvd']
      },

      /**
       * occasion — calibrated to avoid overly broad terms.
       * Removed: 'eternity' from anniversary (also a wedding keyword → ambiguous).
       * Removed: 'tennis' from anniversary (too specific, unreliable signal).
       * Removed: 'solitaire' from engagement (also everyday/pendant style).
       * Kept: 'initial' in birthday (personalised = common gift category).
       */
      occasion: {
        engagement:  ['engagement ring', 'engagement-ring', 'engagement', 'verloving',
                      'proposal', 'aanzoek', 'bridal', 'natural-engagement'],
        wedding:     ['wedding band', 'wedding ring', 'trouwring', 'eternity ring',
                      'eternity band', 'half eternity', 'full eternity',
                      'eternity dot', 'alliance'],
        anniversary: ['anniversary', 'jubileum', 'commemoration',
                      'toi et moi', 'toi-et-moi', 'trilogy ring', 'trilogy pendant'],
        birthday:    ['birthday', 'verjaardag', 'gift', 'cadeau', 'present',
                      'lucky clover', 'hamsa', 'initial pendant', 'initial ring',
                      'personalised', 'initial necklace', 'cutie dot'],
        everyday:    ['daily diamond', 'daily-diamond', 'daily wear', 'minimalist',
                      'dot ring', 'dot necklace', 'stackable', 'flexi-bangle',
                      'flexi bangle', 'cutie dot', 'bar necklace']
      },

      /**
       * style — cross-category: some keys apply to rings, some to earrings/pendants, some to bracelets.
       * The Liquid template uses data-jf-types to show/hide per type in the UI.
       * Here keywords are used for product scoring regardless of type (intentional —
       * a pendant matched to 'halo' style should score high even if type=pendant).
       *
       * Removed: 'invisible' from modern (93/721 products → massive false positive).
       * Removed: 'horizontal' from bar (too generic — 'horizontal oval' is a shape, not bar).
       * Removed: 'drop' alone from drop style ('teardrop' is a shape; 'drop' alone matches
       *   "WaterDrop" earrings, not a hanging-drop design).
       * Fixed: 'pavé' kept in sidestone (sidestones on band; solitaire WITH pavé = sidestone ring).
       */
      style: {
        solitaire:  ['solitaire', 'solitair', 'classic solitaire', 'floating solitaire',
                     'solitaire ring', 'solitaire pendant', 'solitaire bangle',
                     'solitaire flexi', 'fortune solitaire'],
        halo:       ['halo', 'halo ring', 'halo pendant', 'halo stud',
                     'double halo', 'under halo', 'under-halo',
                     'halo dangling', 'halo solitaire', 'halo pavé'],
        vintage:    ['vintage', 'antique', 'art deco', 'milgrain', 'filigree', 'retro'],
        modern:     ['modern', 'contemporary', 'minimalist', 'sleek', 'bezel',
                     'flush', 'special design', 'tension', 'rhombic'],
        threeStone: ['three stone', 'three-stone', 'trilogy', 'trio',
                     'toi et moi', 'toi-et-moi', 'past present future'],
        sidestone:  ['side stone', 'side-stone', 'sidestone', 'with baguettes',
                     'baguette', 'channel', 'pave solitaire', 'pavé solitaire',
                     'pave ring', 'invisible rectangular'],
        drop:       ['dangling earring', 'dangling pendant', 'dangling ring',
                     'drop pendant', 'dangle pendant', 'hanging pendant',
                     'drop tennis earring', 'drop earring', 'round solitaire dangling'],
        cluster:    ['cluster', 'flower diamond', 'invisible flower',
                     'bouquet', 'invisible double halo', 'flakes'],
        bar:        ['bar necklace', 'bar pendant', 'diamond bar necklace',
                     'bar chain'],
        stud:       ['stud', 'studs', 'ear stud', 'diamond stud',
                     'prongs round stud', 'prong stud', 'classic studs',
                     'invisible stud', 'round stud', 'detachable jacket'],
        hoop:       ['hoop', 'hoops', 'diamond hoops', 'classic diamond hoops',
                     'big diamond hoops', 'square diamond hoops'],
        huggie:     ['huggie', 'small hoop', 'tight hoop'],
        tennis:     ['tennis bracelet', 'tennis-bracelet', 'teardrop tennis',
                     'ct teardrop', 'mini tennis', 'flexi tennis', 'degrade tennis',
                     'pear-shape tennis', 'oval cut tennis', 'marquise cut tennis',
                     'emerald tennis', 'ct tennis', 'drop tennis earring'],
        bangle:     ['bangle', 'flexi-bangle', 'twinny bangle', 'split bangle',
                     'open bangle', 'three-layer', 'bangle with diamonds',
                     'solitaire bangle', 'rose bangle', 'invisible flexi'],
        chain:      ['chain bracelet', 'link bracelet', 'schakelarmband'],
        cuff:       ['cuff bracelet', 'open cuff', 'manchet'],
        charm:      ['charm bracelet', 'charms', 'bedel']
      },

      /**
       * metal — keywords for title matching (bracelets often name the metal in title).
       * Variant-level matching uses the fields.metals Set (option2 exact values) via scoreMetal().
       * yellowGold: variant option2 value is exactly "Gold" (not "Yellow Gold").
       * roseGold:   variant option2 value is exactly "Rosé Gold".
       * whiteGold:  variant option2 value is exactly "White Gold".
       */
      metal: {
        yellowGold: ['yellow gold', '18k yellow', '14k yellow',
                     'yellow gold bangle'],
        whiteGold:  ['white gold', '18k white', '14k white',
                     'white gold bangle', 'white gold twinny', 'white gold split',
                     'white gold open'],
        roseGold:   ['rosé gold', 'rose gold',
                     'rosé bangle', 'rose bangle', 'three-layer rosé',
                     'rosé tennis', 'rose tennis', '18k rosé', '14k rosé'],
        // platinum: not stocked — no keywords needed
      },

      /**
       * shape — keywords from actual product titles (English throughout).
       * Removed Dutch words — don't appear in scored title/handle fields.
       * Removed 'rectangular' from emerald — ZIZOV invisible rectangular ≠ emerald cut.
       * Removed 'step cut' — doesn't appear in product titles.
       * Added 'teardrop' to pear — teardrop tennis bracelet uses pear-shaped diamonds.
       * Added 'pearshape' (no dash) — appears in titles like "Invisible Pearshape Diamond Ring".
       */
      shape: {
        round:    ['round', 'round brilliant', 'round cut', 'classic round',
                   'round solitaire', 'round halo', 'prongs round', 'round stud',
                   'round pendant'],
        oval:     ['oval', 'oval cut', 'oval solitaire', 'oval halo',
                   'horizontal oval', 'classic oval'],
        emerald:  ['emerald cut', 'emerald-cut', 'emerald solitaire',
                   'emerald studs', 'classic emerald', 'ct emerald'],
        cushion:  ['cushion cut', 'cushion-cut', 'cushion solitaire',
                   'cushion halo', 'classic cushion'],
        princess: ['princess cut', 'princess-cut', 'square brilliant',
                   'princess solitaire', 'princess studs'],
        pear:     ['pear-shape', 'pear shape', 'pear cut', 'pearshape',
                   'pear solitaire', 'pear halo', 'pear-shape halo',
                   'classic pear-shape', 'teardrop'],
        marquise: ['marquise', 'marquise cut', 'marquise twinny',
                   'invisible marquise', 'lucky marquise', 'marquise-cut',
                   'marquise solitaire'],
        radiant:  ['radiant cut', 'radiant-cut', 'square radiant',
                   'radiant solitaire', 'classic radiant', 'horizontal radiant',
                   'square radiant studs', 'ct radiant'],
        heart:    ['heart-shape', 'heart shape', 'heart solitaire', 'heart cut',
                   'heart diamond', 'classic heart', 'heart pendant']
      }
    },

    /**
     * Budget ranges — aligned with Shopify smart collection tiers (audit 2026-03-09):
     *   Shopify: natural-under-1-499 / natural-1-499-3-499 / natural-3-499-5-999 / natural-5-999
     *   Same tiers apply to lab-grown smart collections.
     *
     * Dynamic default per jewellery type (set in onCardClick when type is chosen):
     *   ring:     index 2 (€3,499–€5,999) — median €4,399 ✓
     *   pendant:  index 1 (€1,499–€3,499) — median €2,499 ✓  (was incorrectly 2 before)
     *   earrings: index 2 (€3,499–€5,999) — median €3,933 ✓
     *   bracelet: index 3 (€5,999–€12,000) — median €7,999 ✓ (was incorrectly 2 before)
     *
     * Prices in Shopify API are EUROS (e.g. "5527.00"); × 100 = cents for range comparison.
     */
    budgetRanges: [
      { label: 'Under €1,499',      min: 0,        max: 149900  },
      { label: '€1,499 – €3,499',   min: 149900,   max: 349900  },
      { label: '€3,499 – €5,999',   min: 349900,   max: 599900  },
      { label: '€5,999 – €12,000',  min: 599900,   max: 1200000 },
      { label: '€12,000 – €25,000', min: 1200000,  max: 2500000 },
      { label: '€25,000+',          min: 2500000,  max: Infinity }
    ],

    /**
     * Default budgetIndex per jewellery type — set dynamically when type is chosen.
     * Falls back to index 2 (€3,499–€5,999) for unknown types.
     */
    budgetDefaultByType: {
      ring:     2,   // median €4,399 → €3,499–€5,999
      pendant:  1,   // median €2,499 → €1,499–€3,499
      earrings: 2,   // median €3,933 → €3,499–€5,999
      bracelet: 3    // median €7,999 → €5,999–€12,000
    }
  };

  /**
   * COLLECTION_META_MAP — maps every collection handle to its jewellery type
   * and (where unambiguous) diamond origin.  Built from the 721-product export.
   *
   * Products are stamped with _jfType / _jfDiamond at fetch time (startFetch).
   * First collection in CFG.collections to yield a product wins (de-duplication),
   * so type-specific collections MUST be listed before cross-category ones.
   *
   * Collections NOT listed here produce products with _jfType = null
   * → those products fall back to keyword-based type detection in scoreJewelleryType.
   *
   * Verified cross-type exceptions (no type stamp to avoid wrong classification):
   *   precious-gemstone-collection → 53R+8P+4E  → type: {} (mixed)
   *   green-emerald               → 24R+7P+1E   → type: {} (mixed)
   *   red-ruby                    → 12R+2E+1P   → type: {} (mixed)
   *   invisible-diamond-collection → 60R+17P+15E+2B → type: {} (cross-type, listed last)
   *   aquamarine-paraiba          → 10R+1P+1B   → excluded entirely (too mixed)
   */
  const COLLECTION_META_MAP = {
    /* ── Rings · Natural ───────────────────────────────────────── */
    'natural-diamond-rings':                        { type: 'ring',     diamond: 'natural'  },
    'natural-engagement-rings':                     { type: 'ring',     diamond: 'natural'  },
    'natural-diamond-solitaire-rings':              { type: 'ring',     diamond: 'natural',  style: 'solitaire'  },
    'natural-diamond-halo-rings':                   { type: 'ring',     diamond: 'natural',  style: 'halo'       },
    'natural-diamond-under-halo-rings':             { type: 'ring',     diamond: 'natural',  style: 'halo'       },
    'natural-diamond-side-stone-rings':             { type: 'ring',     diamond: 'natural',  style: 'sidestone'  },
    'natural-diamond-trilogy-rings':                { type: 'ring',     diamond: 'natural',  style: 'threeStone' },
    'natural-diamond-toi-et-moi-rings':             { type: 'ring',     diamond: 'natural',  style: 'threeStone' },
    'natural-diamond-eternity-rings':               { type: 'ring',     diamond: 'natural'  },
    'natural-round-brilliant-cut-diamond-collection': { type: 'ring',   diamond: 'natural',  shape: 'round'    },
    'natural-oval-cut-diamond-collection':          { type: 'ring',     diamond: 'natural',  shape: 'oval'     },
    'natural-emerald-cut-diamond-collection':       { type: 'ring',     diamond: 'natural',  shape: 'emerald'  },
    'natural-cushion-cut-diamond-collection':       { type: 'ring',     diamond: 'natural',  shape: 'cushion'  },
    'natural-pear-cut-diamond-collection':          { type: 'ring',     diamond: 'natural',  shape: 'pear'     },
    'natural-marquise-cut-diamond-collection':      { type: 'ring',     diamond: 'natural',  shape: 'marquise' },
    'natural-heart-cut-diamond-collection':         { type: 'ring',     diamond: 'natural',  shape: 'heart'    },
    'natural-princess-cut-diamond-collection':      { type: 'ring',     diamond: 'natural',  shape: 'princess' },
    'natural-radiant-cut-diamond-collection':       { type: 'ring',     diamond: 'natural',  shape: 'radiant'  },
    /* ── Rings · Lab-grown ─────────────────────────────────────── */
    'lab-grown-diamond-rings':                      { type: 'ring',     diamond: 'labGrown' },
    'lab-grown-diamond-solitaire-rings':            { type: 'ring',     diamond: 'labGrown', style: 'solitaire'  },
    'lab-grown-diamond-halo-rings':                 { type: 'ring',     diamond: 'labGrown', style: 'halo'       },
    'lab-grown-diamond-under-halo-rings':           { type: 'ring',     diamond: 'labGrown', style: 'halo'       },
    'lab-grown-diamond-side-stone-rings':           { type: 'ring',     diamond: 'labGrown', style: 'sidestone'  },
    'lab-grown-diamond-trilogy-rings':              { type: 'ring',     diamond: 'labGrown', style: 'threeStone' },
    'lab-grown-diamond-toi-et-moi-rings':           { type: 'ring',     diamond: 'labGrown', style: 'threeStone' },
    'lab-grown-diamond-eternity-rings':             { type: 'ring',     diamond: 'labGrown' },
    'exclusive-rings-lg':                           { type: 'ring',     diamond: 'labGrown' },
    'lab-grown-round-brilliant-cut-diamond-collection': { type: 'ring', diamond: 'labGrown', shape: 'round'    },
    'lab-grown-oval-cut-diamond-collection':        { type: 'ring',     diamond: 'labGrown', shape: 'oval'     },
    'lab-grown-emerald-cut-diamond-collection':     { type: 'ring',     diamond: 'labGrown', shape: 'emerald'  },
    'labgrown-cushion-cut-diamond-collection':      { type: 'ring',     diamond: 'labGrown', shape: 'cushion'  },
    'labgrown-pear-cut-diamond-collection':         { type: 'ring',     diamond: 'labGrown', shape: 'pear'     },
    'lab-grown-marquise-cut-diamond-collection':    { type: 'ring',     diamond: 'labGrown', shape: 'marquise' },
    'labgrown-princess-cut-diamond-collection':     { type: 'ring',     diamond: 'labGrown', shape: 'princess' },
    'lab-grown-radiant-cut-diamond-collection':     { type: 'ring',     diamond: 'labGrown', shape: 'radiant'  },
    'fancy-colour-lg':                              { type: 'ring',     diamond: 'labGrown' },
    /* ── Rings · Style / Gemstone (diamond type unknown) ────────── */
    'cocktail-rings':                   { type: 'ring' },
    'design-rings':                     { type: 'ring' },
    'invisible-rings':                  { type: 'ring' },
    'initial-rings':                    { type: 'ring' },
    'marry-by-shape':                   { type: 'ring' },
    'pink-diamond':                     { type: 'ring' },
    'yellow-diamond':                   { type: 'ring' },
    'tanzanite':                        { type: 'ring' },
    'tourmaline':                       { type: 'ring' },
    'garnet-peridot':                   { type: 'ring' },
    'semi-precious-gemstone-collection': { type: 'ring' },
    /* ── Rings · Style-only collections (from live menu) ────────── */
    'classic-solitaire':    { type: 'ring', style: 'solitaire' },
    'halo-solitaire':       { type: 'ring', style: 'halo'      },
    'halo-solitaire-lg':    { type: 'ring', diamond: 'labGrown', style: 'halo' },
    /* ── Rings · Gemstone & Fancy Colour (added 2026-03-09 audit) ── */
    /* gemstone-rings: 97% rings (69R+2E) — earrings already stamped from earring colls above */
    'gemstone-rings':                   { type: 'ring' },
    /* precious-gemstone-collection: mixed 53R+8P+4E — no type stamp to avoid wrong labels */
    'precious-gemstone-collection':     {},
    /* fancy-diamond-collection: 84% rings (21R+2B+1P) — bracelet/pendant already stamped */
    'fancy-diamond-collection':         { type: 'ring' },
    /* green-emerald: mixed 24R+7P+1E — no type stamp */
    'green-emerald':                    {},
    /* blue-sapphire: 87% rings (13R+1E) — earring already stamped */
    'blue-sapphire':                    { type: 'ring' },
    /* red-ruby: mixed 12R+2E+1P — no type stamp */
    'red-ruby':                         {},
    /* ── Cross-type · LAST — only stamps products not yet stamped above ── */
    /* invisible-diamond-collection: 60R+17P+15E+2B — complements invisible-* style colls */
    'invisible-diamond-collection':     {},
    /* ── Pendants · Natural ─────────────────────────────────────── */
    'natural-diamond-pendants':             { type: 'pendant',  diamond: 'natural'  },
    /* ── Pendants · Lab-grown ───────────────────────────────────── */
    'lab-grown-diamond-pendant-collection': { type: 'pendant',  diamond: 'labGrown' },
    'halo-pendants-lg':                     { type: 'pendant',  diamond: 'labGrown', style: 'halo'      },
    'classic-pendants-lg':                  { type: 'pendant',  diamond: 'labGrown', style: 'solitaire' },
    'special-pendants-lg':                  { type: 'pendant',  diamond: 'labGrown' },
    /* ── Pendants · Style (diamond type unknown) ────────────────── */
    'solitaire-pendants':   { type: 'pendant', style: 'solitaire' },
    'design-pendants':      { type: 'pendant' },
    'daily-diamond':        { type: 'pendant' },
    'invisible-pendant':    { type: 'pendant' },
    'initial-pendant':      { type: 'pendant' },
    /* ── Earrings · Natural ─────────────────────────────────────── */
    'natural-diamond-earrings':     { type: 'earrings', diamond: 'natural'  },
    /* ── Earrings · Lab-grown ───────────────────────────────────── */
    'lab-grown-diamond-earrings':   { type: 'earrings', diamond: 'labGrown' },
    'studs-lg':                     { type: 'earrings', diamond: 'labGrown', style: 'stud'  },
    'hoops-lg':                     { type: 'earrings', diamond: 'labGrown', style: 'hoop'  },
    'dangling-lg':                  { type: 'earrings', diamond: 'labGrown', style: 'drop'  },
    /* ── Earrings · Style (diamond type unknown) ────────────────── */
    'studs':             { type: 'earrings', style: 'stud'  },
    'hoops':             { type: 'earrings', style: 'hoop'  },
    'dangle':            { type: 'earrings', style: 'drop'  },
    'invisible-studs':   { type: 'earrings', style: 'stud'  },
    'invisible-earring': { type: 'earrings' },
    'statements':        { type: 'earrings' },
    /* ── Bracelets · Natural ────────────────────────────────────── */
    'natural-diamond-bracelets':    { type: 'bracelet', diamond: 'natural'  },
    /* ── Bracelets · Lab-grown ──────────────────────────────────── */
    'lab-grown-diamond-bracelets':  { type: 'bracelet', diamond: 'labGrown' },
    'tennis-bracelets-lg':          { type: 'bracelet', diamond: 'labGrown', style: 'tennis' },
    'bangles-lg':                   { type: 'bracelet', diamond: 'labGrown', style: 'bangle' },
    /* ── Bracelets · Style (diamond type unknown) ───────────────── */
    'tennis-bracelet':      { type: 'bracelet', style: 'tennis' },
    'bangle':               { type: 'bracelet', style: 'bangle' },
    'invisible-bracelet':   { type: 'bracelet' },
    'champagne-diamond':    { type: 'bracelet' }
  };

  /* ============================================================
     STATE
     ============================================================ */
  const state = {
    step:  0,
    answers: {
      jewelleryType: null,
      diamond:       null,
      occasion:      null,
      style:         null,
      metal:         null,
      shape:         null,
      budgetIndex:   2    // default: €3,499–€5,999 (ring/earrings median); adjusted dynamically per type
    },
    budgetUserSet:   false,  // true once the user manually touches the budget slider
    products:        [],
    fieldsCache:     new Map(),
    fetched:         false,
    fetchP:          null,
    fetchStarted:    null,
    fetchSuccessful: false,
    advancing:       false,
  };

  /** Steps that include a shape selection — all others skip step 6 */
  const SHAPE_TYPES = new Set(['ring', 'pendant']);

  /**
   * Returns the active step sequence based on current jewellery type.
   * Earrings and bracelets skip the shape step (6).
   */
  function getStepSequence() {
    const jt = state.answers.jewelleryType;
    if (jt && jt !== '__skip' && !SHAPE_TYPES.has(jt)) {
      return [1, 2, 3, 4, 5, 7]; // skip shape
    }
    return [1, 2, 3, 4, 5, 6, 7];
  }

  function getTotal()        { return getStepSequence().length; }
  function getPosition(step) { return getStepSequence().indexOf(step) + 1; }
  function getNextStep(cur)  { const s = getStepSequence(); return s[s.indexOf(cur) + 1] ?? null; }
  function getPrevStep(cur)  { const s = getStepSequence(); return s[s.indexOf(cur) - 1] ?? null; }


  /* ============================================================
     UTILITIES
     ============================================================ */
  const qs      = (s, c = document) => c.querySelector(s);
  const qsa     = (s, c = document) => Array.from(c.querySelectorAll(s));
  const noMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * scrollToStep — scrolls only if the step header is above the visible viewport.
   * When the user is already in the right position (step header is visible),
   * no scroll happens — preventing the jarring jump after clicking a card option.
   *
   * @param {boolean} force — force scroll even if header is already visible (used for
   *   explicit Back/Next button clicks and initial quiz start, NOT for auto-advance).
   */
  function scrollToStep(force = false) {
    if (!wrap) return;
    const stepEl = qs('[data-jf-step="' + state.step + '"]', wrap);
    const header = stepEl ? qs('.jf-step__header', stepEl) : null;
    const target = header || stepEl || wrap;

    const rect = target.getBoundingClientRect();
    const MARGIN = 24; // px above the header — just enough breathing room
    const alreadyVisible = rect.top >= MARGIN && rect.top <= window.innerHeight * 0.4;

    if (!force && alreadyVisible) return; // already in a good position — don't scroll

    const scrollY = target.getBoundingClientRect().top + window.scrollY - MARGIN - 64;
    if (noMotion()) { window.scrollTo(0, Math.max(0, scrollY)); return; }
    window.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
  }

  function announce(msg) {
    const el = qs('[data-jf-live]');
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => { el.textContent = msg; });
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function formatPrice(cents) {
    if (!cents || cents === 0) return 'Price on request';
    return (cents / 100).toLocaleString('nl-BE', {
      style: 'currency', currency: 'EUR',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    });
  }

  function minPrice(product) {
    if (!product.variants?.length) return 0;
    const avail = product.variants.filter(v => v.available);
    const pool  = avail.length ? avail : product.variants;
    return Math.min(...pool.map(v => parseFloat(v.price) * 100 || 0));
  }

  function isAvailable(product) {
    return product.variants?.some(v => v.available) ?? true;
  }


  /* ============================================================
     ANALYTICS — GTM / GA4 dataLayer
     ============================================================ */
  function track(event, data = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event:                `jewellery_finder_${event}`,
        jf_step:              state.step,
        jf_jewellery_type:    state.answers.jewelleryType  || undefined,
        jf_diamond:           state.answers.diamond        || undefined,
        jf_occasion:          state.answers.occasion       || undefined,
        jf_style:             state.answers.style          || undefined,
        jf_metal:             state.answers.metal          || undefined,
        jf_shape:             state.answers.shape          || undefined,
        jf_budget:            CFG.budgetRanges[state.answers.budgetIndex]?.label || undefined,
        ...data
      });
    } catch (_) {}
  }


  /* ============================================================
     DOM REFERENCES
     ============================================================ */
  let wrap, hero, stepsnav, steps, skeleton, resultsEl, errorEl, modal;


  /* ============================================================
     SESSION STORAGE — mid-quiz progress (tab-scoped)
     ============================================================ */
  const SS_KEY = 'zizov_jf_v1_progress';

  function saveProgress() {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ step: state.step, answers: state.answers }));
    } catch (_) {}
  }

  function loadProgress() {
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      if (!raw) return false;
      const saved = JSON.parse(raw);
      if (saved?.step > 0 && saved?.answers) {
        state.step    = saved.step;
        state.answers = { ...state.answers, ...saved.answers };
        return true;
      }
    } catch (_) {}
    return false;
  }

  function clearProgress() {
    try { sessionStorage.removeItem(SS_KEY); } catch (_) {}
  }




  /* ============================================================
     URL SHARING
     ============================================================ */
  const URL_MAP = {
    jf_jt: 'jewelleryType',
    jf_d:  'diamond',
    jf_o:  'occasion',
    jf_s:  'style',
    jf_m:  'metal',
    jf_sh: 'shape'
  };

  function generateShareUrl() {
    const p = new URLSearchParams();
    Object.entries(URL_MAP).forEach(([param, key]) => {
      const v = state.answers[key];
      if (v && v !== '__skip') p.set(param, v);
    });
    p.set('jf_b', state.answers.budgetIndex);
    return `${location.origin}${location.pathname}?${p}`;
  }

  function loadFromUrl() {
    const p = new URLSearchParams(location.search);
    if (!p.has('jf_jt') && !p.has('jf_o') && !p.has('jf_b') &&
        !p.has('jf_d')  && !p.has('jf_s') && !p.has('jf_m') && !p.has('jf_sh')) return false;
    Object.entries(URL_MAP).forEach(([param, key]) => {
      const v = p.get(param);
      if (v && VALID_VALUES[key]?.has(v)) state.answers[key] = v;
    });
    const b = parseInt(p.get('jf_b') ?? '', 10);
    if (!isNaN(b) && b >= 0 && b < CFG.budgetRanges.length) state.answers.budgetIndex = b;
    return true;
  }

  async function shareResults() {
    const url = generateShareUrl();
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Selection — ZIZOV Jewellery Finder', url });
        track('share', { jf_method: 'native_share' });
      } else {
        await navigator.clipboard.writeText(url);
        showShareFeedback();
        track('share', { jf_method: 'clipboard' });
      }
    } catch (_) {}
  }

  function showShareFeedback() {
    const btn = qs('[data-jf-share]', wrap);
    if (!btn) return;
    const orig = btn.innerHTML;
    btn.innerHTML = btn.innerHTML.replace(/Share Results/, 'Link Copied!');
    btn.disabled  = true;
    setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2200);
  }


  /* ============================================================
     FOCUS TRAP — modal accessibility (WCAG 2.1 Level A)
     ============================================================ */
  const FOCUSABLE = 'button:not([disabled]),[href],input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
  let _focusTrapHandler  = null;
  let _sliderSaveTimer   = null;

  function enableFocusTrap(el) {
    const nodes = () => Array.from(el.querySelectorAll(FOCUSABLE));
    _focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = nodes();
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
    el.addEventListener('keydown', _focusTrapHandler);
  }

  function disableFocusTrap(el) {
    if (_focusTrapHandler) el.removeEventListener('keydown', _focusTrapHandler);
    _focusTrapHandler = null;
  }


  /* ============================================================
     INITIALISATION
     ============================================================ */
  function init() {
    wrap = qs('#JewelleryFinder');
    if (!wrap) return;

    hero      = qs('.jf-hero',     wrap);
    stepsnav  = qs('.jf-stepsnav', wrap);
    steps     = qsa('.jf-step',    wrap);
    skeleton  = qs('.jf-skeleton', wrap);
    resultsEl = qs('.jf-results',  wrap);
    errorEl   = qs('.jf-error',    wrap);
    modal     = qs('.jf-modal',    wrap);

    if (wrap.dataset.jfAppointmentUrl)   CFG.appointmentUrl    = wrap.dataset.jfAppointmentUrl;
    if (wrap.dataset.jfAllJewelleryUrl)  CFG.allJewelleryUrl   = wrap.dataset.jfAllJewelleryUrl;

    qs('.jf-hero__cta', wrap)?.addEventListener('click', () => {
      startQuiz();
    });


    qsa('.jf-option', wrap).forEach(card => {
      card.addEventListener('click',   onCardClick);
      card.addEventListener('keydown', onCardKey);
    });

    qsa('.jf-nav__btn--back', wrap).forEach(b => b.addEventListener('click', goBack));
    qsa('.jf-nav__btn--next', wrap).forEach(b => b.addEventListener('click', onNext));

    qsa('.jf-stepsnav__item', wrap).forEach((item, i) => {
      item.addEventListener('click', () => {
        const stepNum = parseInt(item.dataset.jfDot, 10);
        if (!stepNum) return;
        const seq = getStepSequence();
        const pos = seq.indexOf(stepNum);
        if (pos !== -1 && pos < seq.indexOf(state.step)) {
          state.step = stepNum;
          showStep(stepNum, true); // stepping back via dot nav → slide in from left
          updateDots();
          scrollToStep(true);
          announce(`Returned to step ${getPosition(stepNum)} of ${getTotal()}.`);
          saveProgress();
          track('step_back', { jf_target_step: stepNum });
        }
      });
      item.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.click(); }
      });
    });

    const slider = qs('.jf-budget__slider', wrap);
    if (slider) slider.addEventListener('input', onSlider);

    qsa('[data-jf-restart]', wrap).forEach(b => b.addEventListener('click', restart));
    qs('[data-jf-share]', wrap)?.addEventListener('click', shareResults);
    qs('[data-jf-save]',  wrap)?.addEventListener('click', saveResults);
    qs('[data-jf-email]', wrap)?.addEventListener('click', openModal);

    // Track main WhatsApp button clicks (not bespoke — those are tracked in showBespoke)
    qs('[data-jf-whatsapp]:not([data-jf-bespoke-wa])', wrap)
      ?.addEventListener('click', () => track('whatsapp_click'));

    // Track empty-state consultation CTA
    qs('.jf-empty__cta', wrap)
      ?.addEventListener('click', () => track('empty_cta_click'));

    if (modal) {
      qs('.jf-modal__close',   modal)?.addEventListener('click', closeModal);
      qs('.jf-modal__overlay', modal)?.addEventListener('click', closeModal);
      qs('.jf-modal__submit',  modal)?.addEventListener('click', onEmailSubmit);
      modal.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });
    }

    qs('.jf-error__retry', wrap)?.addEventListener('click', () => {
      errorEl.classList.remove('is-visible');
      state.fetched        = false;
      state.fetchP         = null;
      state.fetchSuccessful = false;
      showResults();
    });

    const fromUrl = loadFromUrl();

    // ?start — skip hero intro, begin quiz at step 1 with no pre-filled answers.
    // Used by the homepage hero CTA: /pages/zizov-jewellery-finder?start
    const autoStart = new URLSearchParams(location.search).has('start');

    // Only auto-start if coming from a shared URL (explicit intent).
    if (fromUrl) {
      startQuiz(true);
    } else if (autoStart) {
      startFetch();
      startQuiz();
    } else {
      // Pre-fetch products in background while user reads the hero
      setTimeout(startFetch, 1500);
    }
  }



  /* ============================================================
     QUIZ FLOW
     ============================================================ */
  function startQuiz(skipToResults = false, restore = false) {
    hero.style.display = 'none';
    stepsnav.classList.add('is-visible');

    if (skipToResults) { startFetch(); showResults(); return; }

    if (!restore) state.step = 1;
    if (state.step < 1) state.step = 1;

    showStep(state.step);
    updateDots();

    const slider = qs('.jf-budget__slider', wrap);
    if (slider) { slider.value = state.answers.budgetIndex; onSlider({ target: slider }); }

    announce(`Step ${getPosition(state.step)} of ${getTotal()}.`);
    startFetch();
    track('quiz_start');
  }

  function showStep(n, goingBack = false) {
    steps.forEach(s => {
      s.classList.remove('is-active', 'is-going-back');
      s.setAttribute('aria-hidden', 'true');
    });

    const target = qs(`[data-jf-step="${n}"]`, wrap);
    if (!target) return;

    target.classList.add('is-active');
    if (goingBack) target.classList.add('is-going-back');
    target.setAttribute('aria-hidden', 'false');
    // Update aria-label with correct position and total for this user's step sequence.
    // Fixes hardcoded "of 7" being wrong for earrings/bracelets (6 steps only).
    const stepLabel = target.getAttribute('aria-label') || '';
    const stepName  = stepLabel.replace(/Step \d+ of \d+:?\s*/i, '').trim();
    target.setAttribute('aria-label',
      stepName ? `Step ${getPosition(n)} of ${getTotal()}: ${stepName}` : `Step ${getPosition(n)} of ${getTotal()}`
    );

    // Filter style options based on current jewellery type
    if (n === 4) filterTypeOptions(target);

    restoreStepSelections(target);

    target.style.animation = 'none';
    target.offsetHeight;
    target.style.animation = '';

    const firstFocus = qs('.jf-option.is-selected, .jf-option, .jf-budget__slider, .jf-nav__btn', target);
    firstFocus?.focus({ preventScroll: true });

    syncNextBtn();
  }

  /**
   * Filter style options based on jewellery type.
   * Options with data-jf-types attr are shown/hidden based on state.answers.jewelleryType.
   */
  function filterTypeOptions(stepEl) {
    const jt      = state.answers.jewelleryType;
    const showAll = !jt || jt === '__skip';

    qsa('.jf-option[data-jf-types]', stepEl).forEach(card => {
      if (card.classList.contains('jf-option--skip')) return;
      const types   = (card.dataset.jfTypes || '').split(',').map(t => t.trim());
      const visible = showAll || types.includes(jt);
      card.style.display = visible ? '' : 'none';
      if (!visible) {
        card.classList.remove('is-selected');
        card.setAttribute('aria-checked', 'false');
      }
    });

    // If previously selected style doesn't apply to new type, clear it
    if (!showAll) {
      const sel = qs('.jf-option.is-selected[data-jf-key="style"]', stepEl);
      if (sel) {
        const types = (sel.dataset.jfTypes || '').split(',');
        if (!types.includes(jt)) {
          sel.classList.remove('is-selected');
          sel.setAttribute('aria-checked', 'false');
          state.answers.style = null;
          syncNextBtn();
        }
      }
    }
  }

  function restoreStepSelections(stepEl) {
    qsa('.jf-option', stepEl).forEach(card => {
      const key   = card.dataset.jfKey;
      const val   = card.dataset.jfValue;
      if (!key) return;
      const match = state.answers[key] === val;
      card.classList.toggle('is-selected', match);
      card.setAttribute('aria-checked', match ? 'true' : 'false');
    });
  }

  function updateDots() {
    const seq = getStepSequence();
    qsa('.jf-stepsnav__item', wrap).forEach(item => {
      const dotStep = parseInt(item.dataset.jfDot, 10);
      item.classList.remove('is-active', 'is-done', 'is-skipped');

      if (!seq.includes(dotStep)) {
        // Step not in sequence (e.g., shape skipped for earrings)
        item.classList.add('is-skipped');
        item.removeAttribute('tabindex');
        item.removeAttribute('role');
        return;
      }

      const seqPos   = seq.indexOf(dotStep);
      const curPos   = seq.indexOf(state.step);

      if (seqPos < curPos) {
        item.classList.add('is-done');
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'button');
        item.setAttribute('aria-label', `Go back to step ${seqPos + 1}`);
      } else {
        item.removeAttribute('tabindex');
        item.removeAttribute('role');
        item.setAttribute('aria-label', `Step ${seqPos + 1}`);
      }
      if (dotStep === state.step) item.classList.add('is-active');
    });

    const curPos = seq.indexOf(state.step);
    const pct    = seq.length <= 1 ? 0 : (curPos / (seq.length - 1)) * 100;
    stepsnav.style.setProperty('--jf-line-w', pct + '%');

    // Mobile step position indicator (visible only on < 520px where labels are hidden)
    const posEl = qs('[data-jf-step-position]', wrap);
    if (posEl && state.step > 0) {
      posEl.textContent = `Step ${curPos + 1} of ${seq.length}`;
    }
  }

  function onCardClick(e) {
    if (state.advancing) return;

    const card    = e.currentTarget;
    const stepEl  = card.closest('.jf-step');
    const stepNum = parseInt(stepEl.dataset.jfStep, 10);
    const key     = card.dataset.jfKey;
    const val     = card.dataset.jfValue;

    // When jewellery type changes, clear type-specific answers
    if (key === 'jewelleryType' && val !== state.answers.jewelleryType) {
      state.answers.style = null;
      if (val !== '__skip' && !SHAPE_TYPES.has(val)) {
        state.answers.shape = null;
      }

      // Dynamic budget default: adjust slider to the per-category median unless
      // the user has already manually interacted with the budget slider.
      if (!state.budgetUserSet && val !== '__skip') {
        const newDefault = CFG.budgetDefaultByType[val] ?? 2;
        state.answers.budgetIndex = newDefault;
        // Sync slider UI if it exists in the DOM already (avoid setting budgetUserSet)
        const slider = qs('.jf-budget__slider', wrap);
        if (slider) {
          slider.value = newDefault;
          // Call onSlider but temporarily suppress budgetUserSet flag
          const prevUserSet = state.budgetUserSet;
          onSlider({ target: slider });
          state.budgetUserSet = prevUserSet; // restore — this was a programmatic update
        }
      }
    }

    qsa('.jf-option', stepEl).forEach(c => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
    });

    card.classList.add('is-selected');
    card.setAttribute('aria-checked', 'true');
    state.answers[key] = val;

    // Update dots when type changes (sequence might change)
    if (key === 'jewelleryType') updateDots();

    syncNextBtn();
    saveProgress();
    track('step_answer', { jf_question: key, jf_answer: val });

    // Auto-advance on all steps except budget (step 7)
    if (stepNum !== 7) {
      state.advancing = true;
      const nextBtn = qs('.jf-nav__btn--next', stepEl);
      nextBtn?.classList.add('is-advancing');
      setTimeout(() => {
        state.advancing = false;
        nextBtn?.classList.remove('is-advancing');
        goNext(true); // fromCard=true → no forced scroll
      }, 600);
    }
  }

  function onCardKey(e) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); return; }
    const group = qsa('.jf-option:not(.jf-option--skip)', e.currentTarget.closest('.jf-options'));
    const idx   = group.indexOf(e.currentTarget);
    if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && idx < group.length - 1) {
      e.preventDefault(); group[idx + 1].focus();
    }
    if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && idx > 0) {
      e.preventDefault(); group[idx - 1].focus();
    }
  }

  function onNext() {
    const next = getNextStep(state.step);
    next ? goNext() : showResults();
  }

  function goBack() {
    const prev = getPrevStep(state.step);
    if (prev) {
      state.step = prev;
      showStep(state.step, true); // true = going back → slide in from left
      updateDots();
      scrollToStep(true);
      announce(`Went back. Step ${getPosition(state.step)} of ${getTotal()}.`);
      saveProgress();
      track('step_back', { jf_target_step: state.step });
    }
  }

  function goNext(fromCard = false) {
    const next = getNextStep(state.step);
    if (next) {
      state.step = next;
      showStep(state.step);
      updateDots();
      // Auto-advance from card click: only scroll if header is above viewport.
      // Explicit Next button click (fromCard=false): always scroll to orient user.
      scrollToStep(!fromCard);
      announce(`Step ${getPosition(state.step)} of ${getTotal()}.`);
      saveProgress();
      track('step_advance', { jf_to_step: state.step });
    } else {
      showResults();
    }
  }

  function syncNextBtn() {
    const stepEl = qs(`[data-jf-step="${state.step}"]`, wrap);
    if (!stepEl) return;
    const btn = qs('.jf-nav__btn--next', stepEl);
    if (!btn) return;
    const key    = CFG.STEP_KEYS[state.step];
    const locked = key ? state.answers[key] === null : false;
    btn.disabled = locked;
    btn.setAttribute('aria-disabled', locked ? 'true' : 'false');
  }

  function onSlider(e) {
    const val   = parseInt(e.target.value, 10);
    state.answers.budgetIndex = val;
    state.budgetUserSet = true;  // user has manually set budget — don't auto-adjust anymore

    const range = CFG.budgetRanges[val];
    const disp  = qs('.jf-budget__display', wrap);
    if (disp && range) disp.textContent = range.label;

    const pct = (val / (CFG.budgetRanges.length - 1)) * 100;
    e.target.style.background =
      `linear-gradient(to right, var(--rf-accent) ${pct}%, var(--rf-border) ${pct}%)`;

    qsa('.jf-budget__tick', wrap).forEach((tick, i) => {
      tick.classList.toggle('is-active', i <= val);
    });

    e.target.setAttribute('aria-valuenow',  val);
    e.target.setAttribute('aria-valuetext', range?.label ?? '');

    clearTimeout(_sliderSaveTimer);
    _sliderSaveTimer = setTimeout(saveProgress, 300);
  }

  function restart() {
    state.step    = 0;
    state.answers = {
      jewelleryType: null, diamond: null, occasion: null,
      style: null, metal: null, shape: null, budgetIndex: 2
    };
    state.budgetUserSet = false;
    state.advancing = false;
    clearProgress();
    qsa('.jf-option', wrap).forEach(c => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
      c.style.display = ''; // restore any filtered options
    });

    const slider = qs('.jf-budget__slider', wrap);
    if (slider) { slider.value = 2; onSlider({ target: slider }); }

    resultsEl.classList.remove('is-visible', 'is-entering');
    qs('.jf-bespoke', wrap)?.classList.remove('is-visible');
    qs('.jf-empty',   wrap)?.classList.remove('is-visible');
    const grid = qs('.jf-products', wrap); if (grid) grid.innerHTML = '';
    errorEl?.classList.remove('is-visible');
    stepsnav.classList.remove('is-visible');
    hero.style.display = '';

    announce('Quiz restarted.');
    scrollToStep(true);
    setTimeout(() => qs('.jf-hero__cta', wrap)?.focus(), 60);
    track('quiz_restart');
  }





  /* ============================================================
     PRODUCT FETCHING
     ============================================================ */
  async function fetchColl(handle) {
    const out    = [];
    let   page   = 1;
    const MAX_PG = 4; // cap at 4 pages (1,000 products) — no infinite loop
    while (page <= MAX_PG) {
      let res;
      try {
        res = await fetch(
          `/collections/${handle}/products.json` +
          `?limit=${CFG.perPage}&page=${page}&fields=${CFG.fields}`
        );
      } catch (_) { return null; } // network failure — return null sentinel
      if (!res.ok) break;
      let data;
      try { data = await res.json(); } catch (_) { break; }
      if (!data?.products?.length) break;
      out.push(...data.products);
      if (data.products.length < CFG.perPage) break;
      page++;
    }
    return out;
  }

  function startFetch() {
    if (state.fetched || state.fetchP) return;
    state.fetchStarted = Date.now();
    state.fetchP = (async () => {
      /**
       * Two-pass stamping strategy (v1.6):
       *
       * Pass A — type + diamond (first-seen wins, unchanged):
       *   The first type-exclusive collection a product appears in determines
       *   its _jfType and _jfDiamond stamps. Cross-category collections are
       *   listed last so type-exclusive ones always win.
       *
       * Pass B — shape + style (accumulated across ALL collections):
       *   A product may be in BOTH an oval-cut collection (shape) AND a halo
       *   collection (style). We iterate every collection result regardless of
       *   `seen` status and accumulate shape/style into Sets on the product.
       *   Example: oval halo ring → _jfShapes={oval}, _jfStyles={halo}.
       *
       * fieldsCache is built AFTER all stamps are applied (not inside the loop),
       * so getFields() picks up the complete shape/style Sets.
       */
      const results  = await Promise.allSettled(CFG.collections.map(fetchColl));
      const seen     = new Set();
      const out      = [];
      let   successes = 0;

      results.forEach((r, idx) => {
        if (r.status !== 'fulfilled' || !r.value) return;
        successes++;
        const handle = CFG.collections[idx];
        const meta   = COLLECTION_META_MAP[handle] || {};

        r.value.forEach(p => {
          // ── Pass A: type + diamond — first-seen wins ─────────────
          if (!seen.has(p.id)) {
            seen.add(p.id);
            if (meta.type)    p._jfType    = meta.type;
            if (meta.diamond) p._jfDiamond = meta.diamond;
            out.push(p);
          }

          // ── Pass B: shape + style — accumulate across ALL collections ──
          // Runs for EVERY collection the product appears in (not gated by seen).
          if (meta.shape) {
            if (!p._jfShapes) p._jfShapes = new Set();
            p._jfShapes.add(meta.shape);
          }
          if (meta.style) {
            if (!p._jfStyles) p._jfStyles = new Set();
            p._jfStyles.add(meta.style);
          }
        });
      });

      // Build fieldsCache AFTER all stamps applied (shape/style Sets are complete).
      out.forEach(p => state.fieldsCache.set(p.id, buildFields(p)));

      state.products        = out;
      state.fetched         = true;
      state.fetchSuccessful = successes > 0;

        })();
    return state.fetchP;
  }


  /* ============================================================
     SCORING ENGINE
     ============================================================ */
  function buildFields(p) {
    /**
     * Build the scoring fields object for a product.
     *
     * Fields used by scoreCriterion (occasion):
     *   title  (weight 3) — primary signal
     *   handle (weight 2) — slug derived from title
     *
     * Special fields stamped by startFetch — Pass A (first-seen collection):
     *   jfType    — jewellery type (ring/pendant/earrings/bracelet or null)
     *   jfDiamond — diamond origin (natural/labGrown or null)
     *
     * Special fields stamped by startFetch — Pass B (accumulated across all):
     *   jfShapes  — Set of shape values from shape-bearing collections
     *               e.g. Set{'oval'} for oval-cut collection membership
     *   jfStyles  — Set of style values from style-bearing collections
     *               e.g. Set{'halo','sidestone'} if in both collections
     *
     * fields.metals — Set of normalised Option2 material strings
     */
    const metals = new Set();
    (p.variants || []).forEach(v => {
      ['option1', 'option2'].forEach(opt => {
        const raw = (v[opt] || '').trim().toLowerCase();
        const val = raw.replace('rosé', 'rose');
        if (val === 'gold' || val === 'rose gold' || val === 'white gold') {
          metals.add(val);
        }
      });
    });

    return {
      title:     (p.title  || '').toLowerCase(),
      handle:    (p.handle || '').toLowerCase(),
      jfType:    p._jfType    || null,
      jfDiamond: p._jfDiamond || null,
      jfShapes:  p._jfShapes  || null,   // Set<string> from collection stamps, or null
      jfStyles:  p._jfStyles  || null,   // Set<string> from collection stamps, or null
      metals
    };
  }

  function getFields(p) {
    if (state.fieldsCache.has(p.id)) return state.fieldsCache.get(p.id);
    const f = buildFields(p);
    state.fieldsCache.set(p.id, f);
    return f;
  }

  /**
   * Score one criterion with cumulative field bonus.
   * Returns null if skipped, number otherwise.
   */
  function scoreCriterion(fields, key, val) {
    if (!val || val === '__skip') return null;
    const kws = CFG.keywords[key]?.[val];
    if (!kws?.length) return 0;

    let best   = 0;
    let extras = 0;

    Object.entries(CFG.FIELD_WEIGHTS).forEach(([field, weight]) => {
      if (kws.some(kw => fields[field].includes(kw))) {
        if (weight > best) { extras += (best > 0 ? 1 : 0); best = weight; }
        else               { extras++; }
      }
    });

    return best + Math.min(extras * 0.5, 1.5);
  }

  /**
   * Jewellery type scoring — collection-based primary, keyword fallback.
   *
   * Priority 1 (fields.jfType set via COLLECTION_META_MAP):
   *   match   → +4.5  (max possible for this criterion)
   *   mismatch → -4.0  (strong enough to overcome full budget score of +3.0)
   *              The product is also flagged as a confirmed mismatch for the
   *              short-circuit in scoreProduct.
   *
   * Priority 2 (fields.jfType is null — product in no type-exclusive collection):
   *   Fall back to keyword scoring on title + handle ONLY.
   *   Tags excluded (earring tags incorrectly contain "Rings" for 44/45 products).
   *   ' ring' (space-prefixed) prevents false match inside 'earrings'.
   *   '-ring-' checks handles for ring products without 'ring' as a standalone word.
   *   Returns 0 when no keyword evidence found (ambiguous, not penalised).
   *
   * Returns: number (scoring contribution) or null (skipped).
   * Also sets _jfMismatch = true on the product object when a confirmed mismatch
   * is detected, enabling scoreProduct's short-circuit exit.
   */
  function scoreJewelleryType(p, fields, val) {
    if (!val || val === '__skip') return null;

    // ── Priority 1: collection-based (authoritative) ──────────────
    if (fields.jfType) {
      if (fields.jfType === val) {
        p._jfMismatch = false;
        return 4.5;    // perfect match
      } else {
        p._jfMismatch = true;
        return -4.0;   // confirmed wrong type
      }
    }

    // ── Priority 2: keyword fallback (title + handle only) ────────
    // Use only the safe fallback keywords defined in CFG.keywords.jewelleryType.
    // These use space-prefixed patterns (' ring') that can't false-match 'earrings'.
    const kws = CFG.keywords.jewelleryType[val] || [];
    const hit = kws.some(kw =>
      fields.title.includes(kw) || fields.handle.includes(kw)
    );
    if (hit) return 3.0;  // positive keyword signal — good but not perfect

    // Check for a different type to apply a soft penalty
    const others = CFG.JEWELLERY_TYPE_KEYS.filter(k => k !== val);
    for (const other of others) {
      const oKws = CFG.keywords.jewelleryType[other] || [];
      if (oKws.some(kw => fields.title.includes(kw) || fields.handle.includes(kw))) {
        return -2.0;  // soft mismatch (keyword-based, less certain than collection)
      }
    }

    return 0;  // no evidence either way — don't penalise
  }

  /**
   * Diamond type scoring — symmetrical natural vs lab-grown.
   *
   * Priority 1 (fields.jfDiamond set via COLLECTION_META_MAP):
   *   match    → +3.0  (confident signal from collection membership)
   *   mismatch → -2.5  (wrong type — significant penalty)
   *
   * Priority 2 (fields.jfDiamond is null):
   *   labGrown: look for ' (lg)' in title (275 products), '-lg-' in handle.
   *             Also accepts broader 'lab grown', 'lab-grown', 'cvd' etc.
   *   natural:  natural diamonds have no explicit keywords in titles;
   *             absence of lab signals is treated as likely-natural.
   *             Returns +1.5 when no lab signals found (softer than collection match).
   *             Returns -2.0 when lab signals ARE found (user wants natural but product is lab).
   *
   * Max possible: 3.0 (used in scoreProduct maxPossible calculation).
   */
  function scoreDiamond(fields, val) {
    if (!val || val === '__skip') return null;

    // ── Priority 1: collection-based ─────────────────────────────
    if (fields.jfDiamond) {
      if (fields.jfDiamond === val) return 3.0;
      return -2.5;
    }

    // ── Priority 2: keyword fallback ─────────────────────────────
    const lgKws = CFG.keywords.diamond.labGrown;
    const hasLabSignal = lgKws.some(kw =>
      fields.title.includes(kw) || fields.handle.includes(kw)
    );

    if (val === 'labGrown') {
      return hasLabSignal ? 2.5 : -1.5;  // no lab signal → probably natural → penalise
    }
    // val === 'natural'
    return hasLabSignal ? -2.0 : 1.5;    // lab signal found → penalise; absent → likely natural
  }

  /**
   * Shape scoring — collection-first (v1.6), keyword fallback.
   *
   * Priority 1 (fields.jfShapes set via COLLECTION_META_MAP pass B):
   *   match   → +4.5  (product is in the exact shape collection the user wants)
   *   mismatch → -1.5  (product confirmed in a DIFFERENT shape collection)
   *              Softer than type mismatch — a wrong-shape ring with perfect
   *              style/budget still deserves to appear, just ranked lower.
   *
   * Priority 2 (fields.jfShapes is null — no shape-bearing collection found):
   *   Falls back to scoreCriterion keyword matching on title + handle.
   *   Scores 0–4.5 depending on keyword hits.
   */
  function scoreShape(fields, val) {
    if (!val || val === '__skip') return null;

    if (fields.jfShapes) {
      if (fields.jfShapes.has(val)) return 4.5;   // confirmed match via collection
      return -1.5;                                  // confirmed different shape
    }

    // Fallback: keyword scoring on title + handle
    return scoreCriterion(fields, 'shape', val);
  }

  /**
   * Style scoring — collection-first (v1.6), keyword fallback.
   *
   * Priority 1 (fields.jfStyles set via COLLECTION_META_MAP pass B):
   *   match   → +4.5  (product is in the exact style collection the user wants)
   *   mismatch → -1.0  (softer penalty — many products have multiple valid styles,
   *              and style collections are not exhaustive; a ring not in the halo
   *              collection may still BE a halo ring from a cross-listing collection)
   *
   * Priority 2 (fields.jfStyles is null):
   *   Falls back to scoreCriterion keyword matching on title + handle (unchanged).
   */
  function scoreStyle(fields, val) {
    if (!val || val === '__skip') return null;

    if (fields.jfStyles) {
      if (fields.jfStyles.has(val)) return 4.5;   // confirmed match via collection
      return -1.0;                                  // soft mismatch — style collections not exhaustive
    }

    // Fallback: keyword scoring on title + handle
    return scoreCriterion(fields, 'style', val);
  }

  /**
   * Metal scoring — uses exact fields.metals Set (option2 values) as primary signal,
   * with title/handle keyword matching as secondary.
   *
   * Exact Shopify Material option values: 'Gold' (yellow), 'Rosé Gold', 'White Gold'
   * (all lowercase in fields.metals since buildFields normalises with .toLowerCase()).
   *
   * Scoring:
   *   variant match + title mention → 4.5 (best possible)
   *   variant match only            → 3.5
   *   title/handle keyword only     → 2.0
   *   different metal in title      → -1.5 (product is metal-specific in wrong metal)
   *   no evidence                   → 0.0
   *
   * Max possible: 4.5 (used in scoreProduct maxPossible calculation).
   */
  function scoreMetal(fields, val) {
    if (!val || val === '__skip') return null;

    /**
     * Exact option2 material values (after buildFields normalisation to unaccented):
     *   yellow gold → 'gold'
     *   rose gold   → 'rose gold'  (normalised from 'Rosé Gold' or 'Rose Gold')
     *   white gold  → 'white gold'
     */
    const VARIANT_KEY = {
      yellowGold: 'gold',
      roseGold:   'rose gold',
      whiteGold:  'white gold'
    };

    const targetVariantVal = VARIANT_KEY[val];
    const hasVariant = targetVariantVal ? fields.metals.has(targetVariantVal) : false;

    // Check for title / handle keyword match (bracelets often name metal explicitly)
    const kws = CFG.keywords.metal[val] || [];
    const hasTitleMatch = kws.some(kw =>
      fields.title.includes(kw) || fields.handle.includes(kw)
    );

    if (hasVariant && hasTitleMatch) return 4.5;  // strongest signal
    if (hasVariant)                  return 3.5;  // variant confirms metal
    if (hasTitleMatch)               return 2.0;  // title alone (less certain)

    // Check if a DIFFERENT metal is explicitly mentioned in title/handle
    const others = CFG.METAL_KEYS.filter(k => k !== val);
    for (const other of others) {
      const otherKws = CFG.keywords.metal[other] || [];
      if (otherKws.some(kw => fields.title.includes(kw) || fields.handle.includes(kw))) {
        return -1.5;  // product is for a specific different metal
      }
    }

    // No evidence either way — multi-metal ring, neither confirmed nor denied
    return 0;
  }

  /**
   * Budget scoring: exact range = 3 pts, ±15% soft zone = 1.5 pts.
   */
  function scoreBudget(product) {
    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (!range) return 0;
    const pr = minPrice(product);
    if (pr === 0) return 1;
    if (pr >= range.min && pr <= range.max) return 3;
    const softHigh = range.max === Infinity ? Infinity : range.max * 1.15;
    const softLow  = range.min * 0.85;
    if (pr <= softHigh && pr >= softLow) return 1.5;
    return 0;
  }

  function scoreProduct(p) {
    // Exclude concept products (€0 price — internal placeholders, not customer-facing).
    // 5 such products identified in 2026-03-08 export: 'Oorbellen CONCEPT', etc.
    if (minPrice(p) === 0) {
      return { product: p, score: -99, matchPct: 0, matchedCriteria: [] };
    }

    // Reset stale mismatch flag from any previous scoring run.
    // Without this, changing type selection from e.g. 'ring' → 'no preference'
    // would leave _jfMismatch=true on pendants/earrings from the prior run,
    // incorrectly excluding them from the no-preference results.
    p._jfMismatch = false;

    const fields          = getFields(p);
    const matchedCriteria = [];
    let totalScore  = 0;
    let maxPossible = 0;

    // ── 1. Jewellery Type (highest priority) ─────────────────────────
    // scoreJewelleryType now takes product object to set p._jfMismatch flag.
    const jtScore = scoreJewelleryType(p, fields, state.answers.jewelleryType);
    if (jtScore !== null) {
      maxPossible += 4.5;  // fixed max (collection match = 4.5, keyword match = 3.0)
      totalScore  += jtScore;
      if (jtScore > 0) matchedCriteria.push('jewelleryType');
    }

    // Short-circuit: confirmed collection-based type mismatch → exclude immediately.
    // This prevents budget/style scores rescuing wrong-type products.
    if (p._jfMismatch === true) {
      return { product: p, score: -99, matchPct: 0, matchedCriteria: [] };
    }

    // ── 2. Diamond type ───────────────────────────────────────────────
    const diamondScore = scoreDiamond(fields, state.answers.diamond);
    if (diamondScore !== null) {
      maxPossible += 3.0;   // collection match = 3.0, keyword = 2.5
      totalScore  += diamondScore;
      if (diamondScore > 0) matchedCriteria.push('diamond');
    }

    // ── 3. Occasion — pure bonus: adds to score but NOT to maxPossible ─────
    // Rationale: zero products have 'engagement' in title; occasion keywords cover
    // very few products (~14-64 per occasion). Adding to maxPossible would dilute
    // matchPct for products that simply don't have occasion-specific keywords —
    // which is the vast majority. Occasion is kept as a positive signal only.
    const occasionScore = scoreCriterion(fields, 'occasion', state.answers.occasion);
    if (occasionScore !== null) {
      totalScore += occasionScore;   // adds bonus when matched
      // maxPossible intentionally NOT increased — occasion is a bonus, not a requirement
      if (occasionScore > 0) matchedCriteria.push('occasion');
    }

    // ── 4. Style — collection-first (v1.6), keyword fallback ─────────────
    const styleScore = scoreStyle(fields, state.answers.style);
    if (styleScore !== null) {
      maxPossible += 4.5;  // collection match = 4.5
      totalScore  += styleScore;
      if (styleScore > 0) matchedCriteria.push('style');
    }

    // ── 5. Shape — collection-first (v1.6), keyword fallback ─────────────
    const shapeScore = scoreShape(fields, state.answers.shape);
    if (shapeScore !== null) {
      maxPossible += 4.5;  // collection match = 4.5
      totalScore  += shapeScore;
      if (shapeScore > 0) matchedCriteria.push('shape');
    }

    // ── 6. Metal ──────────────────────────────────────────────────────
    const metalScore = scoreMetal(fields, state.answers.metal);
    if (metalScore !== null) {
      maxPossible += 4.5;  // variant + title match = 4.5
      totalScore  += metalScore;
      if (metalScore > 0) matchedCriteria.push('metal');
    }

    // ── 7. Budget ─────────────────────────────────────────────────────
    maxPossible += 3;
    const budgetScore = scoreBudget(p);
    totalScore  += budgetScore;
    if (budgetScore >= 1.5) matchedCriteria.push('budget');

    const matchPct = maxPossible > 0
      ? Math.round(Math.min((totalScore / maxPossible) * 100, 100))
      : 0;

    return { product: p, score: totalScore, matchPct, matchedCriteria };
  }

  /**
   * Rank products by score and return top maxResults.
   *
   * When all quiz questions are skipped (hasAnswers = false), we skip score
   * filtering entirely and return the top 24 by availability then price — so
   * the results page is never empty just because the user skipped everything.
   *
   * When questions are answered, confirmed type-mismatches score -99 and are
   * excluded by the score > 0 filter.
   */
  function rankProducts() {
    const scored = state.products.map(scoreProduct);

    // Detect whether the user answered at least one question
    const hasAnswers = Object.entries(state.answers).some(([k, v]) => {
      if (k === 'budgetIndex') return false;  // budget always has a default value
      return v !== null && v !== '__skip';
    });

    const pool = hasAnswers
      ? scored.filter(r => r.score > 0)
      : scored.filter(r => r.score > -99); // exclude confirmed mismatches even in fallback

    return pool
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Secondary sort: available first, then by ascending price
        const aA = isAvailable(a.product) ? 0 : 1;
        const bA = isAvailable(b.product) ? 0 : 1;
        if (aA !== bA) return aA - bA;
        return minPrice(a.product) - minPrice(b.product);
      })
      .slice(0, CFG.maxResults);
  }


  /* ============================================================
     SHOW RESULTS
     ============================================================ */
  async function showResults() {
    steps.forEach(s => { s.classList.remove('is-active'); s.setAttribute('aria-hidden', 'true'); });
    stepsnav.classList.remove('is-visible');
    errorEl?.classList.remove('is-visible');

    if (state.answers.budgetIndex === CFG.BESPOKE_INDEX) { showBespoke(); return; }

    skeleton?.classList.add('is-visible');
    scrollToStep(true);
    announce('Searching our collection for your perfect match…');

    if (!state.fetched) startFetch();
    const fetchStart = state.fetchStarted || Date.now();

    try { await state.fetchP; } catch (_) {
      skeleton?.classList.remove('is-visible');
      errorEl?.classList.add('is-visible');
      scrollToStep(true); track('results_error'); return;
    }

    if (!state.fetchSuccessful) {
      skeleton?.classList.remove('is-visible');
      errorEl?.classList.add('is-visible');
      scrollToStep(true); track('results_error'); return;
    }

    const elapsed = Date.now() - fetchStart;
    if (elapsed < 700) await new Promise(r => setTimeout(r, 700 - elapsed));

    skeleton?.classList.remove('is-visible');

    const ranked = rankProducts();
    renderResults(ranked);

    resultsEl.classList.add('is-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      resultsEl.classList.remove('is-entering');
      resultsEl.classList.add('is-visible');
    }));

    clearProgress();
    announce(
      ranked.length === 0
        ? 'No items found. Book a free consultation for personal advice.'
        : `${ranked.length} item${ranked.length !== 1 ? 's' : ''} matched your preferences.`
    );
    track('results_shown', { jf_result_count: ranked.length });
  }

  function showBespoke() {
    skeleton?.classList.remove('is-visible');
    const bespokeEl = qs('.jf-bespoke', wrap);
    if (!bespokeEl) return;

    // Clear count text and hide summary tags — not relevant for bespoke tier
    const countEl = qs('.jf-results__count', wrap);
    if (countEl) countEl.textContent = '';
    const summaryEl = qs('.jf-results__summary', wrap);
    if (summaryEl) summaryEl.innerHTML = '';

    const cta = qs('.jf-bespoke__cta', bespokeEl);
    if (cta) {
      cta.href = CFG.appointmentUrl;
      cta.setAttribute('aria-label', 'Request Private Consultation (opens in new window)');
      cta.addEventListener('click', () => track('bespoke_consultation_click'), { once: true });
    }
    const waBtn = qs('[data-jf-bespoke-wa]', bespokeEl);
    if (waBtn) {
      waBtn.addEventListener('click', () => track('bespoke_whatsapp_click'), { once: true });
    }
    updateWhatsappLink();
    bespokeEl.classList.add('is-visible');
    resultsEl.classList.add('is-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      resultsEl.classList.remove('is-entering');
      resultsEl.classList.add('is-visible');
    }));
    scrollToStep(true);
    announce('Your preferences suggest a bespoke creation. Our Antwerp experts await.');
    track('results_bespoke');
  }


  /* ============================================================
     RENDER RESULTS
     ============================================================ */
  const FALLBACK = 'https://cdn.shopify.com/s/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif';

  function imgSrc(raw, w) {
    if (!raw || raw.includes('no-image')) return raw || FALLBACK;
    return raw.replace(/(\.(jpg|jpeg|png|webp|gif))(\?|$)/i, `_${w}x$1$3`);
  }

  function renderResults(ranked) {
    renderTags();

    const countEl   = qs('.jf-results__count',  wrap);
    const grid      = qs('.jf-products',         wrap);
    const emptyEl   = qs('.jf-empty',            wrap);
    const bespokeEl = qs('.jf-bespoke',          wrap);

    if (countEl) {
      countEl.textContent = ranked.length > 0
        ? `${ranked.length} item${ranked.length !== 1 ? 's' : ''} matched your preferences`
        : '';
    }

    bespokeEl?.classList.remove('is-visible');
    if (!grid) return;

    if (ranked.length === 0) {
      grid.innerHTML = '';
      if (emptyEl) {
        emptyEl.classList.add('is-visible');
        const cta = qs('.jf-empty__cta', emptyEl);
        if (cta) {
          cta.href = CFG.appointmentUrl;
          cta.setAttribute('aria-label', 'Book a Free Consultation (opens in new window)');
        }
      }
      return;
    }

    if (emptyEl) emptyEl.classList.remove('is-visible');

    grid.innerHTML = ranked.map(({ product: p, matchPct, matchedCriteria }, i) => {
      const available  = isAvailable(p);
      const raw1       = p.images?.[0]?.src ?? FALLBACK;
      const raw2       = p.images?.[1]?.src ?? null;
      const variantId  = findVariantForMetal(p, state.answers.metal);
      const price      = minPrice(p);
      // Use the variant matched for the selected metal for compare_at_price.
      // Falls back to variants[0] if no metal-matched variant is found.
      const matchedVariant = variantId
        ? (p.variants || []).find(v => v.id === variantId) ?? p.variants?.[0]
        : p.variants?.[0];
      const compare    = parseFloat(matchedVariant?.compare_at_price ?? 0) * 100;
      const hasDisc    = compare > price && compare > 0;
      const productUrl = variantId
        ? `/products/${esc(p.handle)}?variant=${variantId}`
        : `/products/${esc(p.handle)}`;

      const priceHtml = hasDisc
        ? `<span class="is-sale">${formatPrice(price)}</span><s>${formatPrice(compare)}</s>`
        : formatPrice(price);

      let badge = '', badgeClass = '';
      if      (matchPct >= 90) { badge = '★ Perfect Match'; badgeClass = 'jf-product__badge--perfect'; }
      else if (matchPct >= 75) { badge = '✦ Great Match';   badgeClass = 'jf-product__badge--great';   }
      else if (matchPct >= 60) { badge = 'Good Match';      badgeClass = 'jf-product__badge--good';    }

      const breakdown = matchedCriteria.map(k => {
        if (k === 'budget') return CFG.budgetRanges[state.answers.budgetIndex]?.label ?? 'Budget';
        const val = state.answers[k];
        return LABELS[k]?.[val] ?? val;
      }).filter(Boolean).join(' · ');

      const imgHtml = raw2
        ? `<img src="${esc(imgSrc(raw1,600))}" srcset="${esc(imgSrc(raw1,600))} 1x, ${esc(imgSrc(raw1,1200))} 2x" alt="${esc(p.title)}" class="jf-product__img jf-product__img--primary" loading="lazy" width="400" height="400">
           <img src="${esc(imgSrc(raw2,600))}" srcset="${esc(imgSrc(raw2,600))} 1x, ${esc(imgSrc(raw2,1200))} 2x" alt="" class="jf-product__img jf-product__img--alt" loading="lazy" width="400" height="400">`
        : `<img src="${esc(imgSrc(raw1,600))}" srcset="${esc(imgSrc(raw1,600))} 1x, ${esc(imgSrc(raw1,1200))} 2x" alt="${esc(p.title)}" class="jf-product__img" loading="lazy" width="400" height="400">`;

      const delay = Math.min(i * 0.055, 0.5).toFixed(2);

      return `
        <a href="${productUrl}"
           role="listitem" class="jf-product${!available ? ' is-sold-out' : ''}"
           aria-label="${esc(p.title)}${!available ? ' (sold out)' : ''}"
           style="animation-delay:${delay}s"
           data-jf-available="${available ? '1' : '0'}"
           data-handle="${esc(p.handle)}">
          <div class="jf-product__img-wrap">
            ${imgHtml}
            ${badge ? `
              <span class="jf-product__badge ${badgeClass}" aria-label="${badge}. Matched: ${esc(breakdown)}">
                ${badge}
                ${breakdown ? `<span class="jf-product__badge-tip">${esc(breakdown)}</span>` : ''}
              </span>` : ''}
            ${!available ? `<div class="jf-product__sold-out"><span>Sold Out</span></div>` : ''}
          </div>
          <div class="jf-product__info">
            <h3 class="jf-product__title">${esc(p.title)}</h3>
            <p class="jf-product__price">${priceHtml}</p>
          </div>
        </a>`;
    }).join('');

    applyProductVisibility();
    initBadgeTooltips();

    // ── Product click tracking ──────────────────────────────────────────
    const productGrid = qs('.jf-results__grid', wrap);
    if (productGrid) {
      productGrid.addEventListener('click', e => {
        const card = e.target.closest('.jf-product');
        if (!card) return;
        track('product_click', {
          jf_product_handle: card.dataset.handle || '',
          jf_product_title:  card.getAttribute('aria-label') || '',
        });
      });
    }

    // ── WhatsApp CTA: update href with pre-filled quiz summary ──────────
    updateWhatsappLink();
  }

  /**
   * Badge tooltips — desktop: CSS :hover / :focus-within.
   * Mobile (touch): tap the badge to toggle. Tap anywhere else to dismiss.
   * The tooltip is already in the DOM via renderResults — just needs event wiring.
   */
  function initBadgeTooltips() {
    const grid = qs('.jf-products', wrap);
    if (!grid) return;

    // Single delegated listener on the grid — badges may be re-rendered
    grid.addEventListener('click', function onBadgeClick(e) {
      const badge = e.target.closest('.jf-product__badge');
      if (!badge) {
        // Tap outside → close all
        qsa('.jf-product__badge.is-tip-open', grid).forEach(b => b.classList.remove('is-tip-open'));
        return;
      }
      // This is only needed on touch devices; skip on pointer:fine (mouse)
      if (!window.matchMedia('(pointer: coarse)').matches) return;
      e.preventDefault();
      const wasOpen = badge.classList.contains('is-tip-open');
      qsa('.jf-product__badge.is-tip-open', grid).forEach(b => b.classList.remove('is-tip-open'));
      if (!wasOpen) badge.classList.add('is-tip-open');
    }, { passive: false });
  }

  function applyProductVisibility() {
    // All products on Zizov are in stock — no sold-out filtering needed.
    // Function kept for structural compatibility (called after renderResults).
  }

  function findVariantForMetal(product, metalKey) {
    if (!metalKey || metalKey === '__skip' || !product.variants?.length) return null;

    /**
     * Zizov product export has two option layouts:
     *   Layout A (most products): Option1=Size(55,56...), Option2=Material(Gold/Rosé Gold/White Gold)
     *   Layout B (bracelets/pendants with length): Option1=Material, Option2=Length(40,45,50cm)
     *
     * Bug fix v1.6: original code used v.option2 ?? v.option1 which fails for Layout B
     * because option2 exists (as a length value) and is NOT null, so option1 (the metal) is never reached.
     * Fixed: inspect BOTH option slots and use whichever one contains a recognised metal value.
     */
    const METAL_VALUES = new Set(['gold', 'rose gold', 'white gold']);
    const normalise = (s) => ((s ?? '') + '').toLowerCase().trim().replace('rosé', 'rose');

    function getMetalOption(v) {
      const o1 = normalise(v.option1);
      const o2 = normalise(v.option2);
      if (METAL_VALUES.has(o2)) return o2;
      if (METAL_VALUES.has(o1)) return o1;
      return '';
    }

    const TARGET = { yellowGold: 'gold', roseGold: 'rose gold', whiteGold: 'white gold' };
    const target = TARGET[metalKey];

    // Priority 1: exact metal option match (layout-agnostic)
    const exactMatch = product.variants.find(v => getMetalOption(v) === target);
    if (exactMatch) return exactMatch.id;

    // Priority 2: keyword search in full variant title
    const kws = CFG.keywords.metal[metalKey] || [];
    const kwMatch = product.variants.find(v => {
      const title = normalise(v.title);
      if (metalKey === 'yellowGold') {
        return title.includes('yellow gold') ||
               (title.includes('gold') && !title.includes('rose') && !title.includes('white'));
      }
      return kws.some(kw => title.includes(kw.replace('rosé', 'rose')));
    });
    return kwMatch?.id ?? null;
  }


  /* ============================================================
     RENDER TAGS — uses LABELS, no duplication
     ============================================================ */
  function renderTags() {
    const container = qs('.jf-results__summary', wrap);
    if (!container) return;

    const tags = [];

    ['jewelleryType', 'diamond', 'occasion', 'style', 'metal', 'shape'].forEach(key => {
      const val = state.answers[key];
      if (!val || val === '__skip' || !LABELS[key]?.[val]) return;
      tags.push(`<span class="jf-results__tag">${esc(LABELS[key][val])}</span>`);
    });

    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (range) tags.push(`<span class="jf-results__tag">${esc(range.label)}</span>`);

    container.innerHTML = tags.join('');
  }

  function buildPreferencesSummary() {
    const lines = [];
    ['jewelleryType', 'diamond', 'occasion', 'style', 'metal', 'shape'].forEach(key => {
      const val = state.answers[key];
      if (val && val !== '__skip' && LABELS[key]?.[val]) lines.push(`${key}: ${LABELS[key][val]}`);
    });
    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (range) lines.push(`budget: ${range.label}`);
    return lines.join(', ');
  }


  /* ============================================================
     EMAIL MODAL
     ============================================================ */
  function openModal() {
    if (!modal) return;
    const form    = qs('.jf-modal__form',    modal);
    const success = qs('.jf-modal__success', modal);
    form?.classList.remove('is-hidden');
    success?.classList.remove('is-visible');
    const submit = qs('.jf-modal__submit', modal);
    if (submit) { submit.disabled = false; submit.textContent = 'Send My Results'; }

    modal.hidden = false;
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      setTimeout(() => qs('#jfModalName, #jfModalEmail', modal)?.focus(), 60);
    });

    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = sbw > 0 ? sbw + 'px' : '';
    document.body.style.overflow = 'hidden';
    enableFocusTrap(modal);
    track('email_modal_open');
  }

  function closeModal() {
    if (!modal) return;
    disableFocusTrap(modal);
    modal.classList.remove('is-open');
    document.body.style.paddingRight = '';
    document.body.style.overflow = '';
    setTimeout(() => { modal.hidden = true; }, 280);
    qs('[data-jf-email]', wrap)?.focus();
  }

  async function onEmailSubmit() {
    const nameInput  = qs('#jfModalName',  modal);
    const emailInput = qs('#jfModalEmail', modal);
    const submitBtn  = qs('.jf-modal__submit', modal);
    const form       = qs('.jf-modal__form',   modal);
    const success    = qs('.jf-modal__success', modal);

    const name  = nameInput?.value.trim()  || '';
    const email = emailInput?.value.trim() || '';

    if (!email || !email.includes('@')) {
      emailInput?.focus();
      emailInput?.classList.add('is-error');
      setTimeout(() => emailInput?.classList.remove('is-error'), 2000);
      return;
    }

    submitBtn.disabled    = true;
    submitBtn.textContent = 'Sending…';

    const prefs = buildPreferencesSummary();

    wrap.dispatchEvent(new CustomEvent('zizovJFLeadCapture', {
      bubbles: true,
      detail:  { name, email, preferences: prefs, results: generateShareUrl() }
    }));

    track('email_capture', { jf_preferences: prefs });

    try {
      const fd = new FormData();
      fd.append('form_type',        'contact');
      fd.append('utf8',             '✓');
      fd.append('contact[name]',    name || 'ZIZOV Jewellery Finder User');
      fd.append('contact[email]',   email);
      fd.append('contact[body]',
        `ZIZOV Jewellery Finder — Personalised Selection\n\nPreferences: ${prefs}\n\nResults: ${generateShareUrl()}`
      );
      await fetch('/contact', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
    } catch (_) {}

    form.classList.add('is-hidden');
    success.classList.add('is-visible');
    announce('Thank you! Check your inbox soon.');
  }


  /* ============================================================
     SAVE RESULTS — copy shareable URL to clipboard
     ============================================================ */
  function saveResults() {
    const url = generateShareUrl();
    const btn = qs('[data-jf-save]', wrap);
    if (!btn) return;

    navigator.clipboard.writeText(url).then(() => {
      const orig = btn.innerHTML;
      btn.innerHTML = btn.innerHTML.replace(/Save Results/, 'Link Copied!');
      btn.disabled = true;
      setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2400);
      track('save_results', { jf_method: 'clipboard' });
    }).catch(() => {
      // Fallback: select + copy via execCommand
      const el = document.createElement('textarea');
      el.value = url;
      el.style.cssText = 'position:fixed;opacity:0;';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(el);
      const orig = btn.innerHTML;
      btn.innerHTML = btn.innerHTML.replace(/Save Results/, 'Link Copied!');
      btn.disabled = true;
      setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2400);
    });
  }


  /* ============================================================
     WHATSAPP CTA — pre-filled message with quiz answers
     ============================================================ */
  function generateWhatsappMessage() {
    const lines = ['Hi ZIZOV! 💎 I used the Jewellery Finder and found my preferences:'];
    const labelMap = {
      jewelleryType: 'Type',
      diamond:       'Diamond',
      occasion:      'Occasion',
      style:         'Style',
      metal:         'Metal',
      shape:         'Shape'
    };
    Object.entries(labelMap).forEach(([key, display]) => {
      const val = state.answers[key];
      if (val && val !== '__skip' && LABELS[key]?.[val]) {
        lines.push(`• ${display}: ${LABELS[key][val]}`);
      }
    });
    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (range) lines.push(`• Budget: ${range.label}`);
    lines.push('');
    lines.push('Can you help me find the perfect piece? 🙏');
    return lines.join('\n');
  }

  function updateWhatsappLink() {
    const msg = generateWhatsappMessage();
    qsa('[data-jf-whatsapp]', wrap).forEach(btn => {
      const base = btn.dataset.jfWaBase || btn.getAttribute('href') || '';
      if (!base || base === '#') return;
      btn.href = `${base}?text=${encodeURIComponent(msg)}`;
    });
  }


  /* ============================================================
     BOOT
     ============================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
